import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { clampPage, Pager } from '../../../core/index.js'
import type { Posting, PostingStage, ScheduleBoardView } from '../../types.js'
import { unitNameOf, type UnitNames } from '../boardText.js'
import { shortDay } from '../censusCardData.js'
import { heldSeats, openSeatsInFlight, type TodayFilter } from './todayData.js'
import { PostingAudiencePanel } from './PostingAudiencePanel.js'
import { useBridgeCallouts } from '../../hooks.js'
import { calloutForPosting, calloutLine } from './postingCallout.js'
import { DemoPlay } from './DemoPlay.js'

/** The stage of a seat's life (open shift lifecycle plan section 3), the same in either fill mode. */
const STAGE_LABEL: Record<PostingStage, string> = { waiting: 'Waiting', posted: 'Posted', urgent: 'Urgent', critical: 'Needs you', started: 'Started', ended: 'Over' }
const STAGE_CLASS: Record<PostingStage, string> = { waiting: 'bg-silver-lt text-ink-mid', posted: 'bg-marine-lt text-marine-dk', urgent: 'bg-gold-lt text-ink', critical: 'bg-coral-lt text-coral', started: 'bg-coral-lt text-coral', ended: 'bg-silver-lt text-ink-mid' }
const STAGE_ORDER: readonly PostingStage[] = ['critical', 'urgent', 'posted', 'waiting']
const SOURCE_LABEL: Record<string, string> = { unfilled: 'left open by the build', vacated: 'vacated', bridge_callout: 'from a Bridge callout', census_rise: 'census rise', demo: 'the two-minute demo' }

/** Rows to a page (the user's call, 27 October 2026: pages, not a list that grows). */
const PAGE_SIZE = 6

function offeredText(posting: Posting, names: ReadonlyMap<string, string>): string {
  if (posting.phase === 'first_come' || (posting.blastAt && posting.phase === 'locked')) {
    if (!posting.blastAt) {
      const digested = new Set((posting.digested ?? []).map((entry) => entry.workerId)).size
      return digested > 0 ? `Open first come · in ${digested} ${digested === 1 ? 'worker’s' : 'workers’'} daily digest` : 'Open to everyone eligible, first come'
    }
    const at = new Date(posting.blastAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    const said = posting.declined?.length ?? 0
    const again = posting.repeats?.length ?? 0
    return `${posting.told ? `${posting.told.length} told` : 'Everyone eligible told'} at once at ${at}${again > 0 ? ` · ${again === 1 ? 'repeated once' : `repeated ${again} times`}` : ''}${said > 0 ? ` · ${said} can't make it` : ''}`
  }
  const live = posting.offers.filter((offer) => offer.answer === null)
  if (live.length > 0) {
    return `Offered to ${live.map((offer) => names.get(offer.workerId) ?? offer.workerId).join(', ')}`
  }
  return posting.offers.length > 0 ? `${posting.offers.length} offered, none took it` : posting.phase === 'locked' ? 'Yours to assign' : 'Not offered yet'
}

/**
 * The open seats in flight (facility operations dashboard plan §3, §3b): every open posting on the released month in
 * the order it locks, with its phase, who it is offered to now, and the seats held for a recall. Only eligible workers
 * are ever offered a seat; the board decides who that is.
 */
export function OpenShiftsInFlight({ board, filter, names, units = new Map(), canOpenCallout = false, demo = null }: { board: ScheduleBoardView | null; filter: TodayFilter; names: ReadonlyMap<string, string>; units?: UnitNames; canOpenCallout?: boolean; demo?: { facilityId: string; period: string } | null }) {
  const [page, setPage] = useState(0)
  // The one row opened onto who was told (who was told design, 26 October 2026).
  const [opened, setOpened] = useState<string | null>(null)
  const callouts = useBridgeCallouts(board?.facilityId ?? null)
  const open = openSeatsInFlight(board, filter)
  const held = heldSeats(board, filter)
  // The two-minute demo (open shift lifecycle plan section 11.3): a demo client's presenter plays a seat from here.
  const play = demo && board ? <DemoPlay facilityId={demo.facilityId} period={demo.period} units={[...units.entries()].map(([id, name]) => ({ id, name }))} board={board} /> : null
  if (!board) {
    return <p className="text-xs text-ink-mid">The open board starts with the month’s release.</p>
  }
  if (open.length === 0 && held.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {play}
        <p className="text-xs text-ink-mid">No open seats: every shift the month holds is filled.</p>
      </div>
    )
  }
  const counts = STAGE_ORDER.map((stage) => [stage, open.filter((posting) => posting.stage === stage).length] as const).filter(([, count]) => count > 0)
  // One list, the open seats in lock order then the held ones, shown a page at a time; a page past the end (the filter narrowed the list) falls back to the last.
  const rows = [...open.map((posting) => ({ kind: 'open' as const, posting })), ...held.map((seat) => ({ kind: 'held' as const, seat }))]
  const current = clampPage(page, rows.length, PAGE_SIZE)
  const shown = rows.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE)
  const shownOpen = shown.flatMap((row) => (row.kind === 'open' ? [row.posting] : []))
  const shownHeld = shown.flatMap((row) => (row.kind === 'held' ? [row.seat] : []))
  return (
    <div className="flex flex-col gap-2">
      {play}
      <div className="flex flex-wrap gap-1.5">
        {counts.map(([stage, count]) => (
          <span key={stage} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STAGE_CLASS[stage]}`}>
            {STAGE_LABEL[stage]} {count}
          </span>
        ))}
        {held.length > 0 && <span className="rounded-full bg-silver-lt px-2 py-0.5 text-[10px] font-bold text-ink-mid">Held for recall {held.length}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[34rem] text-xs">
          <thead>
            <tr className="bg-marine-dk text-left text-[10px] font-bold uppercase tracking-wide text-white/70">
              <th className="px-2 py-1.5">Shift</th>
              <th className="px-2 py-1.5">Unit · role</th>
              <th className="px-2 py-1.5">Phase</th>
              <th className="px-2 py-1.5">Now</th>
            </tr>
          </thead>
          <tbody>
            {shownOpen.map((posting) => {
              const isOpen = opened === posting.postingId
              return (
                <Fragment key={posting.postingId}>
                  <tr className={`border-b border-marine/10 ${isOpen ? 'bg-canvas' : ''}`}>
                    <td className="px-2 py-1.5 font-bold text-ink">
                      {shortDay(posting.shift.date)} · {posting.shift.startTime}
                    </td>
                    <td className="px-2 py-1.5">
                      {unitNameOf(posting.shift.unitId, units)} · {posting.shift.role ?? 'any role'}
                      <span className="block text-[10px] text-ink-mid">{SOURCE_LABEL[posting.source] ?? posting.source}</span>
                      {(() => {
                        // A callout the board posted as this seat is the seat's source already; one opened for the seat is news.
                        const callout = posting.source === 'bridge_callout' ? null : calloutForPosting(posting, callouts.data)
                        return callout ? <span className="mt-0.5 inline-block rounded-full bg-marine-lt px-2 py-0.5 text-[10px] font-bold text-marine-dk">{calloutLine(callout, (workerId) => names.get(workerId) ?? workerId)}</span> : null
                      })()}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STAGE_CLASS[posting.stage]}`}>{STAGE_LABEL[posting.stage]}</span>
                    </td>
                    <td className="px-2 py-1.5 text-ink-mid">
                      <button type="button" aria-expanded={isOpen} className="inline-flex items-center gap-1 text-left font-bold text-marine hover:text-marine-dk" onClick={() => setOpened(isOpen ? null : posting.postingId)}>
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                        {offeredText(posting, names)}
                        <span className="sr-only">: who was told, and what you can do</span>
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-marine/10 bg-canvas">
                      <td colSpan={4} className="px-2 pb-3">
                        <PostingAudiencePanel facilityId={board.facilityId} period={board.period} postingId={posting.postingId} canOpenCallout={canOpenCallout} vacatedFrom={posting.vacatedFrom} calloutId={posting.calloutId} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
            {shownHeld.map((seat) => (
              <tr key={`held-${seat.shiftId}`} className="border-b border-marine/10">
                <td className="px-2 py-1.5 font-bold text-ink">
                  {shortDay(seat.date)} · {seat.startTime}
                </td>
                <td className="px-2 py-1.5">
                  {unitNameOf(seat.unitId, units)} · {seat.role ?? 'any role'}
                </td>
                <td className="px-2 py-1.5">
                  <span className="rounded-full bg-silver-lt px-2 py-0.5 text-[10px] font-bold text-ink-mid">Held</span>
                </td>
                <td className="px-2 py-1.5 text-ink-mid">
                  {names.get(seat.workerId) ?? seat.workerId}’s first right until {new Date(seat.recallUntil).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* On the right, where the plays' pager sits above it (the user's call, 28 October 2026). */}
      <div className="flex justify-end">
        <Pager total={rows.length} pageSize={PAGE_SIZE} page={current} onPage={setPage} label="open shifts" />
      </div>
    </div>
  )
}
